import * as React from 'react';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import { Source, Layer } from 'react-map-gl';
import MapGL from 'react-map-gl';
import { useSize } from 'react-hook-size';
import { ViewState } from 'react-map-gl-draw/node_modules/@types/react-map-gl';

import _ from "lodash";

import ESRI_BASE from '../../../components/Map/styles/esri-base.json';
import ESRI_BASE_SATELLITE from '../../../components/Map/styles/esri-base-satellite.json';
import { MAPBOX_BASE_SATELLITE } from '../../../components/Map/styles/mapbox-base-satellite';
import ESRI_BASE_LABELS from '../../../components/Map/styles/esri-base-labels.json';
import NAIP from '../../../components/Map/styles/naip.json';

import { editFieldsLayer, fieldsBorderLayer, parcelFillLayer, parcelsBorderLayer, parcelSelectedLayer } from '../../../components/Map/styles/fields';
import { createFeatureCollection, createFeatureFromField, getViewport } from '../../../utils/mapUtils';

import { Field, LayerVisibility } from '../../../types/types';
import EditFieldForm from './EditFieldForm';
import { useQuery } from 'react-apollo';
import { GET_FIELDS } from '../../../../../queries';
import { GetFields } from '../../../../../__generated__/types';
import { EditingMode, Editor } from 'react-map-gl-draw';

import {NavigationControl} from 'react-map-gl';

import '../../../styles/map-gl.scss';
import { SelectControlPanel } from './SelectControlPanel';
import { getEditHandleStyle, getFeatureStyle } from '../../../components/Map/styles/draw';
import { useFetch } from '../../../../../hooks/useFetch';
import { useParams } from 'react-router-dom';
import { generatePath } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import LayerVisibilityControlPanel from './LayerVisibilityControlPanel';

const navControlStyle= {
    left: 10,
    top: 10
};

const initLayerVisibility: LayerVisibility[] = [{
    layer_id: 'naip',
    layer_name: 'Historical Imagery',
    is_visible: true,
    order: 2,
    source: NAIP,
},{
    layer_id: 'MAPBOX',
    layer_name: 'Mapbox Imagery',
    is_visible: true,
    order: 3,
    source: MAPBOX_BASE_SATELLITE,
},{
    layer_id: 'ESRI_LABELS',
    layer_name: 'Labels',
    is_visible: true,
    order: 1,
    source: ESRI_BASE_LABELS,
}];

const REGRID_URL = `https://tiles.regrid.com/api/v1/parcels?format=mvt&token=${process.env.REACT_APP_REGRID_TOKEN}`;
const FIELD_APP_URL = process.env.REACT_APP_FIELD_MGR_ROOT_URL;

export const SelectField = () => {
    const history = useHistory();

    const mapContainerRef = useRef(null);
    const editorRef = useRef<any>(null);
    const { vp } = useParams();

    const { width, height } = useSize(mapContainerRef);

    const [viewport, setViewport] = useState<ViewState>(getViewport(vp));
    const [fieldsDataSource, setFieldsDataSource] = useState(null);
    const [selectedFeatureId, setSelectedFeatureId] = useState<number | null>(null);
    const [selectedFeature, setSelectedFeature] = useState<any>(null);
    const [parcelSource, setParcelSource] = useState<{} | null>(null);
    const [mode, setMode] = useState<EditingMode | undefined>(undefined);
    const [editFeature, setEditFeature] = useState<any>(null);
    const [editField,] = useState<Field | null>(null);
    const [overlayVisibility, setOverlayVisible] = useState<LayerVisibility[]>(initLayerVisibility);

    const {response: parcelResponse } = useFetch(REGRID_URL, {});
    const {data: fieldData} = useQuery<GetFields>(GET_FIELDS, { fetchPolicy: "no-cache" });

    useEffect(() => {
        if(parcelResponse) {
            setParcelSource({
                "type": "vector",
                "tiles": parcelResponse.tiles ? parcelResponse.tiles : [],
                "tileSize": 512,
                "attribution": "",
                "maxzoom": 24,
                "minzoom": 12
            });
        }
    }, [parcelResponse])

    useEffect(() => {
        if(fieldData) {
            // convert the fields to a feature collection
            const fields = fieldData.landscan_fields.map((f:any) => {
                return {...f,id: f.field_id} as Field;
            });

            const features = fields.map((f:Field) => createFeatureFromField(f));
            const featureCollection = createFeatureCollection(features);

            setFieldsDataSource(featureCollection);
        }
    }, [fieldData]);

    useEffect(() => {
        console.log("A040724 overlayVisibility", overlayVisibility);
    }, [overlayVisibility]);

    const onClick = (event: any) => {
        console.log('selected feature from map', event, event.features);

        if(event.features.length) {
            const feature = event.features[0];
            const currentlySelected = feature.properties.fid === selectedFeatureId;

            setSelectedFeatureId(currentlySelected ? null : feature.properties.fid);
            setSelectedFeature(currentlySelected ? null : JSON.parse(JSON.stringify(feature)));
        }
    };

    const selectedId = selectedFeatureId || '';
    const selectedFilter = useMemo(() => ['in', 'fid', selectedId], [selectedId]);

    const copySelected = () => {
        if(!selectedFeature) {
            return;
        }

        // copy the feature into editable source.
        setEditFeature([selectedFeature]);
        setMode(new EditingMode());
    }

    const copyCleared = () => {
        if(editorRef.current) {
            editorRef.current.deleteFeatures(0);
        }

        setEditFeature(null);
        setSelectedFeature(null);
        setSelectedFeatureId(null);
        setMode(undefined);
    }

    const onUpdate = useCallback((event) => {
        const { editType, data } = event;
        console.log('onUpdate', editType, data);

        setEditFeature(data);
    }, []);

    const delayedUrlUpdate = React.useCallback(_.debounce((url:any) => history.replace(url), 250), []);

    const onInteractionStateChange = (e: any) => {
        if(!e.isDragging && !e.isPanning && !e.isZooming) {
            const str = `@${viewport.latitude.toFixed(5)},${viewport.longitude.toFixed(5)},${viewport.zoom.toFixed(2)}`;
            const url = generatePath(`${FIELD_APP_URL}/:vp/fields/select`, {vp: str});
              
            delayedUrlUpdate(url);
        }
    }

    const getCursor = () => {
        return 'pointer';
    }

    return (
        <div className="d-flex h-100">
            <div className="bg-light border border-bottom-0 border-dark border-top-0 d-flex flex-column flex-shrink-0 shadow-sm text-dark" style={{ width: '300px' }}>
               {<EditFieldForm fieldToEdit={editField} feature={editFeature}/>}
            </div>
            <div className="flex-fill bg-white position-relative" ref={mapContainerRef}>
                <MapGL
                    viewState={{
                        ...viewport,
                        maxZoom:25
                    }}
                    width='100%'
                    height='100%'
                    getCursor={() => getCursor()}
                    doubleClickZoom={false}
                    mapStyle={ESRI_BASE}
                    onClick={onClick}
                    interactiveLayerIds={parcelSource ? ['parcel-fill'] : []}
                    onInteractionStateChange={(s:any) => onInteractionStateChange(s)} 
                    onViewportChange={(v:ViewState) => setViewport(v)}> 
                    {
                    overlayVisibility.map((layer: LayerVisibility) => (
                        //console.log(layer),
                        <Source 
                            key={`s-${layer.layer_id}`}
                            id={layer.layer_id}
                            type='raster'
                            tileSize={256}
                            tiles={layer.source.sources['raster-tiles'].tiles}>
                            <Layer 
                                key={layer.layer_id} 
                                id={layer.layer_id} 
                                type='raster' 
                                source={layer.layer_id} 
                                maxzoom={layer.source.layers[0].maxzoom} 
                                minzoom={layer.source.layers[0].minzoom} 
                                layout={{ visibility: layer.is_visible ? 'visible' : 'none' }} 
                            />
                        </Source>
                        ))
                    }

                    { parcelSource && 
                        <Source {...parcelSource}>
                            <Layer {...parcelsBorderLayer} layout={{visibility: editFeature && editFeature[0] ? 'none' : 'visible'}} />
                            <Layer {...parcelFillLayer} layout={{visibility: editFeature && editFeature[0] ? 'none' : 'visible'}} />
                            <Layer {...parcelSelectedLayer} layout={{visibility: editFeature && editFeature[0] ? 'none' : 'visible'}} filter={selectedFilter} />
                        </Source>
                    }

                    <Source type="geojson" data={fieldsDataSource}>
                        <Layer {...editFieldsLayer} />
                        <Layer {...fieldsBorderLayer} />
                    </Source>

                    <Editor
                        ref={editorRef}
                        style={{ width: '100%', height: '100%' }}
                        clickRadius={12}
                        mode={mode}
                        onUpdate={onUpdate}
                        features={editFeature}
                        editHandleShape={'circle'}
                        featureStyle={getFeatureStyle}
                        editHandleStyle={getEditHandleStyle}
                    />

                    <NavigationControl style={navControlStyle} showCompass={false} />
                </MapGL>
                <div style={{position: 'absolute', bottom:'10px', left:'10px'}}>
                    <LayerVisibilityControlPanel showDatePicker={false} layers={overlayVisibility} onChange={(visibility: LayerVisibility[]) => setOverlayVisible(visibility)} />
                </div>
                <SelectControlPanel selectedFeature={selectedFeature} copiedFeature={editFeature} onCopy={copySelected} onClear={copyCleared} />
            </div>
        </div>
    );
}
